import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import {
	ColumnModel,
	DataModel,
	fileBooleanMappings,
	FormActionTypeEnum,
	PrimaryKeyValueModel,
	TableTypeEnum,
	getPrimaryKeys
} from '@shared';
import { FormColumnCustomOptions, FormControlBase, ITFormControlType } from '../models';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { FormControlFactoryService } from '../factories';
import { QueryParamsProvider } from '@domain/routing';
import { map, switchMap } from 'rxjs/operators';
import { NormalizeDatabaseValuesService } from './normalize-database-values.service';
import { WorkspaceRepository } from '@database/central';
import { BusinessLayerService, SamplesService, TableTypeProvider } from '../../business-layer';
import { PreviousRecordService } from './previous-record.service';
import { InheritedValuesService } from './inherited-values/inherited-values.service';

@Injectable()
export class FormControlsBuilderService {
	private readonly excludedProperties: string[] = [WorkspaceDatabaseKeys.columns.projectNumber];

	public constructor(
		private formControlFactory: FormControlFactoryService,
		private queryParamsProvider: QueryParamsProvider,
		private dbValuesNormalizer: NormalizeDatabaseValuesService,
		private workspaceRepository: WorkspaceRepository,
		private businessLayerService: BusinessLayerService,
		private samplesService: SamplesService,
		private tableTypeProvider: TableTypeProvider,
		private previousRecordService: PreviousRecordService,
		private inheritedValuesService: InheritedValuesService
	) {}

	public get isFormActionTypeNewFrom(): boolean {
		return this.queryParamsProvider.formActionType() === FormActionTypeEnum.newFrom;
	}

	public build$(columns: ColumnModel[]): Observable<FormControlBase<ITFormControlType>[]> {
		const formActionType = this.queryParamsProvider.formActionType();
		const parentTableName = this.queryParamsProvider.parentTableName;
		const tableName = this.queryParamsProvider.childTableName ?? parentTableName;
		const projectNumber = this.queryParamsProvider.projectNumber;
		const workfileId = this.queryParamsProvider.workfileId;
		const entryId = this.queryParamsProvider.childEntryId
			? Number(this.queryParamsProvider.childEntryId)
			: Number(this.queryParamsProvider.entryId);
		const parentEntryId = Number(this.queryParamsProvider.entryId);

		const filteredColumns = this.preProcessColumns(columns);

		switch (formActionType) {
			case FormActionTypeEnum.duplicate:
				return this.buildDupModeControl(
					filteredColumns,
					tableName,
					projectNumber,
					entryId,
					parentEntryId,
					parentTableName,
					workfileId
				);
			case FormActionTypeEnum.newFrom:
				return this.buildNewFromFormControl(
					filteredColumns,
					tableName,
					projectNumber,
					entryId,
					parentEntryId,
					parentTableName,
					workfileId
				);
			case FormActionTypeEnum.edit:
				return this.buildEditModeControl(filteredColumns, tableName, projectNumber, entryId);
			default:
				return this.buildControl(
					filteredColumns,
					workfileId,
					projectNumber,
					parentTableName,
					parentEntryId,
					tableName
				);
		}
	}

	private buildEditModeControl(
		columns: ColumnModel[],
		tableName: string,
		projectNumber: string,
		entryId: number
	): Observable<FormControlBase<ITFormControlType>[]> {
		return this.workspaceRepository
			.getEntityPrimaryKey$(this.queryParamsProvider.workfileId, this.queryParamsProvider.parentTableName)
			.pipe(
				switchMap((primaryKeys: string[]) =>
					this.dbValuesNormalizer
						.prepareEditFormValues$(columns, tableName, projectNumber, entryId, primaryKeys)
						.pipe(
							map((row: { formValueModel: DataModel; controlCustomOptions: FormColumnCustomOptions }) =>
								columns.map((column: ColumnModel) => {
									const columnValue: ITFormControlType = row.formValueModel[column.column_name];
									return this.formControlFactory.getFormControl(
										column,
										primaryKeys,
										columnValue,
										row.controlCustomOptions[column.column_name]
									);
								})
							)
						)
				)
			);
	}

	private buildControl(
		columns: ColumnModel[],
		workfileId: string,
		projectNumber: string,
		parentTableName: string,
		parentEntryId: number,
		tableName: string
	): Observable<FormControlBase<ITFormControlType>[]> {
		//todo: move this logic in a service (normalizedDatabase.service)
		return this.businessLayerService
			.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, parentEntryId)
			.pipe(
				switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
					combineLatest([
						this.tableTypeProvider.getTableType$(tableName, workfileId).pipe(
							switchMap((tableTypes: string[]) => {
								if (
									!(
										tableTypes.includes(TableTypeEnum.sampleTable) ||
										tableTypes.includes(TableTypeEnum.standardTable)
									)
								) {
									return of(null);
								}

								return this.samplesService.getNewSampleNumber$(projectNumber, primaryKeyValue);
							})
						),
						this.previousRecordService.getPreviousRecord$(tableName, projectNumber, primaryKeyValue)
					]).pipe(
						map(([sampleNumber, previousRecord]) => {
							const listOfPrimaryKeys = getPrimaryKeys(primaryKeyValue);

							return columns.map((column: ColumnModel) => {
								const controlCustomOptions: FormColumnCustomOptions = {};
								const previousRecordColumn: ColumnModel = this.handleDifferentInheritColumnTypes(
									columns,
									column
								);

								const overwritableColumnValue = this.inheritedValuesService.getPrePopulated(
									column,
									previousRecord,
									previousRecordColumn
								);
								const primaryKey: PrimaryKeyValueModel | undefined = primaryKeyValue.find(
									(item: PrimaryKeyValueModel) => item.primaryKey === column.column_name
								);

								let columnValue =
									listOfPrimaryKeys.includes(column.column_name) && primaryKey.primaryKeyValue
										? this.dbValuesNormalizer.prepareFormValue(column, primaryKey.primaryKeyValue)
										: overwritableColumnValue;

								// primaryKeyValue is available when editing a parent record or creating/editing a child record
								if (listOfPrimaryKeys.includes(column.column_name) && primaryKey.primaryKeyValue) {
									controlCustomOptions[column.column_name] = { disabled: true };
								}

								// for scenario "flat table with samples"
								if (
									listOfPrimaryKeys.includes(column.column_name) &&
									!primaryKeyValue &&
									parentTableName !== tableName
								) {
									controlCustomOptions[column.column_name] = { disabled: true };
								}

								if (
									sampleNumber &&
									(column.column_name === WorkspaceDatabaseKeys.columns.standardNumber ||
										column.column_name === WorkspaceDatabaseKeys.columns.sampleNumber)
								) {
									columnValue = sampleNumber;
								}

								return this.formControlFactory.getFormControl(
									column,
									listOfPrimaryKeys,
									columnValue,
									controlCustomOptions[column.column_name]
								);
							});
						})
					)
				)
			);
	}

	private buildDupModeControl(
		columns: ColumnModel[],
		tableName: string,
		projectNumber: string,
		entryId: number,
		parentEntryId: number,
		parentTableName: string,
		workfileId: string
	): Observable<FormControlBase<ITFormControlType>[]> {
		return this.businessLayerService
			.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, parentEntryId)
			.pipe(
				switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
					this.dbValuesNormalizer
						.prepareDupFormValues$(columns, tableName, projectNumber, entryId, primaryKeyValue)
						.pipe(
							map((row: { formValueModel: DataModel; controlCustomOptions: FormColumnCustomOptions }) =>
								columns.map((column: ColumnModel) => {
									const columnValue: ITFormControlType = row.formValueModel[column.column_name];
									return this.formControlFactory.getFormControl(
										column,
										getPrimaryKeys(primaryKeyValue),
										columnValue,
										row.controlCustomOptions[column.column_name]
									);
								})
							)
						)
				)
			);
	}

	private buildNewFromFormControl(
		columns: ColumnModel[],
		tableName: string,
		projectNumber: string,
		entryId: number,
		parentEntryId: number,
		parentTableName: string,
		workfileId: string
	): Observable<FormControlBase<ITFormControlType>[]> {
		return this.businessLayerService
			.getPrimaryKeyAndValue$(workfileId, projectNumber, parentTableName, parentEntryId)
			.pipe(
				switchMap((primaryKeyValue: PrimaryKeyValueModel[]) =>
					this.dbValuesNormalizer.prepareNewFromValues$(columns, tableName, projectNumber, entryId).pipe(
						map((row: { formValueModel: DataModel; controlCustomOptions: FormColumnCustomOptions }) =>
							columns.map((column: ColumnModel) => {
								const columnValue: ITFormControlType = row.formValueModel[column.column_name];
								return this.formControlFactory.getFormControl(
									column,
									getPrimaryKeys(primaryKeyValue),
									columnValue,
									row.controlCustomOptions[column.column_name]
								);
							})
						)
					)
				)
			);
	}

	private isColumnDisplayed(displayColumnValue: string): boolean {
		return Boolean(fileBooleanMappings[displayColumnValue]);
	}

	private preProcessColumns(columns: ColumnModel[]): ColumnModel[] {
		const filteredColumns = columns
			.filter(
				(column: ColumnModel) =>
					this.isColumnDisplayed(column.display_column) &&
					!this.excludedProperties.includes(column.column_name)
			)
			.sort((a, b) => a.display_order - b.display_order);

		return filteredColumns;
	}

	private handleDifferentInheritColumnTypes(allColumns: ColumnModel[], column: ColumnModel): ColumnModel {
		if (column?.inherit_values_column && column?.inherit_values_column !== '') {
			const inheritColumnLowerCase = column?.inherit_values_column.toLowerCase();
			return allColumns.find((item: ColumnModel) => item.column_name.toLowerCase() === inheritColumnLowerCase);
		}
	}
}
